// Who pays what for a charge: the payer and the patient (docs/plans/phase1-finance-catalogue.md §5.3).
//
// The one invariant: payer amount + patient amount = total, exactly, for every method,
// quantity and sign. The rounded number is always the one the rule is about (the patient's
// percentage co-pay); the other share is what is left, as in `applyDiscount` (ADR 0006).
//
// Rules come from scheme configuration. None is invented here: this module only applies
// a rule it is given, and `selectSplitRule` reports `split.no_rule` rather than assuming
// "payer pays all" when a scheme has none. That matters most for SHA, whose split
// behaviour is TODO(verify-spec) (plan open question 6).

import type { ServiceItemCategory } from './catalogue.ts';
import type { EffectivePeriod } from './effective-period.ts';
import { periodContains } from './effective-period.ts';
import type { Money } from './money.ts';
import {
  applyRate,
  minMoney,
  moneyFromCents,
  multiplyMoney,
  ONE_HUNDRED_PERCENT,
  subtractMoney,
  zeroMoney,
} from './money.ts';
import type { BasisPoints } from './money.ts';
import type { Result } from './result.ts';
import { err, ok } from './result.ts';

export const SPLIT_METHODS = [
  'payer_pays_all',
  'patient_pays_all',
  'patient_copay_percent',
  'patient_copay_fixed_per_unit',
  'payer_limit_per_unit',
] as const;

export type SplitMethod = (typeof SPLIT_METHODS)[number];

/** The terms of a rule, with exactly the parameters its method uses. */
export type SplitTerms =
  | { readonly method: 'payer_pays_all' }
  | { readonly method: 'patient_pays_all' }
  | { readonly method: 'patient_copay_percent'; readonly copayRate: BasisPoints }
  | { readonly method: 'patient_copay_fixed_per_unit'; readonly amountPerUnit: Money }
  | { readonly method: 'payer_limit_per_unit'; readonly amountPerUnit: Money };

export interface ChargeSplit {
  readonly total: Money;
  readonly payerAmount: Money;
  readonly patientAmount: Money;
  readonly method: SplitMethod;
}

function assertNotNegative(amount: Money, label: string): void {
  if (amount.cents < 0n) throw new RangeError(`${label} cannot be negative`);
}

/**
 * Splits `unitPrice × quantity` between payer and patient.
 *
 * The unit price is never negative (prices have a CHECK); a negative quantity is a return
 * or a credit and splits as the exact negative of the same positive quantity, so a credit
 * note reverses each party's share to the cent. The arithmetic is done on the magnitude
 * and the sign reapplied, which is what makes that true for the capped methods too.
 */
export function splitCharge(
  unitPrice: Money,
  quantity: bigint | number,
  terms: SplitTerms,
): ChargeSplit {
  assertNotNegative(unitPrice, 'Unit price');
  const total = multiplyMoney(unitPrice, quantity);
  const negative = total.cents < 0n;
  const magnitude = moneyFromCents(negative ? -total.cents : total.cents, total.currency);
  const units = typeof quantity === 'bigint' ? quantity : BigInt(quantity);
  const unitCount = units < 0n ? -units : units;

  const patient = patientShareOfMagnitude(magnitude, unitCount, terms);
  const payer = subtractMoney(magnitude, patient);

  const sign = (amount: Money): Money =>
    negative ? moneyFromCents(-amount.cents, amount.currency) : amount;
  return {
    total,
    payerAmount: sign(payer),
    patientAmount: sign(patient),
    method: terms.method,
  };
}

function patientShareOfMagnitude(magnitude: Money, unitCount: bigint, terms: SplitTerms): Money {
  switch (terms.method) {
    case 'payer_pays_all':
      return zeroMoney(magnitude.currency);
    case 'patient_pays_all':
      return magnitude;
    case 'patient_copay_percent': {
      const rate = terms.copayRate;
      if (!Number.isInteger(rate) || rate < 0 || rate > ONE_HUNDRED_PERCENT)
        throw new RangeError(
          `Co-pay rate must be 0 to ${String(ONE_HUNDRED_PERCENT)} basis points`,
        );
      return applyRate(magnitude, rate);
    }
    case 'patient_copay_fixed_per_unit': {
      assertNotNegative(terms.amountPerUnit, 'Co-pay amount');
      return minMoney(multiplyMoney(terms.amountPerUnit, unitCount), magnitude);
    }
    case 'payer_limit_per_unit': {
      assertNotNegative(terms.amountPerUnit, 'Payer limit');
      const payer = minMoney(multiplyMoney(terms.amountPerUnit, unitCount), magnitude);
      return subtractMoney(magnitude, payer);
    }
  }
}

// --- Choosing the rule -------------------------------------------------------------

export interface SplitRule {
  readonly id: string;
  readonly payerId: string;
  /** Null: the payer's rule for every scheme. */
  readonly schemeId: string | null;
  /** Null: every category. At most one of `category` and `serviceItemId` is set. */
  readonly category: ServiceItemCategory | null;
  /** Null: every item. */
  readonly serviceItemId: string | null;
  readonly terms: SplitTerms;
  readonly period: EffectivePeriod;
  readonly status: 'active' | 'entered_in_error';
}

export interface SplitRuleQuery {
  readonly payerId: string;
  readonly schemeId: string | null;
  readonly serviceItemId: string;
  readonly category: ServiceItemCategory;
  readonly at: Date;
}

export type SplitErrorCode = 'split.no_rule' | 'split.overlapping_rules';

/** English descriptions; the API and the web app own the wording shown to a user. */
export const SPLIT_ERRORS: Record<SplitErrorCode, string> = {
  'split.no_rule': 'Who pays for this is not set up for this payer or scheme',
  'split.overlapping_rules':
    'Two rules say who pays for this at the same time. An administrator must correct one',
};

/**
 * Specificity for this query, or null when the rule doesn't apply.
 * 6: scheme + item, 5: scheme + category, 4: scheme, 3: payer + item, 2: payer + category, 1: payer.
 */
function splitRuleRank(rule: SplitRule, q: SplitRuleQuery): number | null {
  if (rule.status !== 'active' || rule.payerId !== q.payerId) return null;
  if (rule.category !== null && rule.serviceItemId !== null)
    throw new RangeError('A split rule names a category or an item, not both');
  if (rule.schemeId !== null && rule.schemeId !== q.schemeId) return null;
  if (rule.serviceItemId !== null && rule.serviceItemId !== q.serviceItemId) return null;
  if (rule.category !== null && rule.category !== q.category) return null;
  if (!periodContains(rule.period, q.at)) return null;
  const target = rule.serviceItemId !== null ? 3 : rule.category !== null ? 2 : 1;
  return rule.schemeId === null ? target : target + 3;
}

/**
 * The most specific active rule in effect at `q.at`. Two rules at the same, highest
 * specificity are reported as `split.overlapping_rules`, not settled by picking one: the
 * database's exclusion constraint should make that impossible, and if it happens anyway
 * the charge must stop rather than bill by chance.
 */
export function selectSplitRule(
  rules: readonly SplitRule[],
  q: SplitRuleQuery,
): Result<SplitRule, SplitErrorCode> {
  let best: SplitRule[] = [];
  let bestRank = 0;
  for (const rule of rules) {
    const rank = splitRuleRank(rule, q);
    if (rank === null) continue;
    if (rank > bestRank) {
      best = [rule];
      bestRank = rank;
    } else if (rank === bestRank) {
      best.push(rule);
    }
  }
  if (best.length > 1) return err('split.overlapping_rules');
  const rule = best[0];
  return rule ? ok(rule) : err('split.no_rule');
}
